import { useEffect } from 'react';
import { Check, Dna, Loader2, RotateCcw, Sparkles, SquareArrowOutUpRight, X } from 'lucide-react';
import clsx from 'clsx';
import { useEvolve } from '../../store/evolveStore';
import { useStudio } from '../../store/studioStore';
import { TEXT_TECHNIQUE_ID } from '../../store/layer';
import { getTechnique } from '../../techniques/registry';
import { Row, Toggle } from '../controls/Basics';
import { Slider } from '../controls/Slider';

/**
 * Interactive evolution.
 *
 * The whole interaction is two verbs: choose, and go on. Everything else — crossover,
 * mutation rate, which parameters may move — is either configuration or handled by
 * `evolve/genetics.ts`, and none of it needs to be understood to use the mode.
 *
 * Choosing nothing is a legitimate move and means "none of these, try again from the
 * same starting point", which is why the button stays enabled with an empty selection.
 */
export function EvolveStudio(): JSX.Element {
  const doc = useStudio((s) => s.doc);
  const replaceDocument = useStudio((s) => s.replaceDocument);

  const config = useEvolve((s) => s.config);
  const base = useEvolve((s) => s.base);
  const generation = useEvolve((s) => s.generation);
  const population = useEvolve((s) => s.population);
  const progress = useEvolve((s) => s.progress);
  const lineage = useEvolve((s) => s.lineage);
  const thumbSize = useEvolve((s) => s.thumbSize);
  const setConfig = useEvolve((s) => s.setConfig);
  const setThumbSize = useEvolve((s) => s.setThumbSize);
  const seedFrom = useEvolve((s) => s.seedFrom);
  const advance = useEvolve((s) => s.advance);
  const toggleChosen = useEvolve((s) => s.toggleChosen);
  const chooseNone = useEvolve((s) => s.chooseNone);
  const reset = useEvolve((s) => s.reset);

  const chosen = population.filter((v) => v.favourite).length;
  const running = progress.status === 'running';
  const done = population.filter((v) => v.blob || v.error).length;

  // Enter breeds the next generation — the only shortcut the mode needs, and it makes
  // the loop "click a few, press Enter" without leaving the keyboard.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const t = e.target;
      if (t instanceof HTMLElement && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      if (e.key === 'Enter' && base && !running) {
        e.preventDefault();
        advance();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [base, running, advance]);

  return (
    <div className="flex min-h-0 flex-1">
      {/* ── Controls ──────────────────────────────────────────────────────── */}
      <aside className="w-[268px] shrink-0 overflow-y-auto border-r border-ink-800 bg-ink-900 px-3 py-3">
        <div className="mb-3 flex items-center gap-2">
          <Dna size={14} className="text-accent-500" />
          <span className="text-[12px] font-medium text-ink-100">Evolution</span>
          {generation > 0 && (
            <span className="ml-auto rounded bg-ink-800 px-1.5 py-0.5 font-mono text-[10px] text-ink-400">
              Gen {generation}
            </span>
          )}
        </div>

        <p className="mb-3 text-[10px] leading-relaxed text-ink-500">
          Wähle die Bilder, die dir gefallen. Die nächste Generation wird aus genau diesen gekreuzt und mutiert —
          ohne einen einzigen Regler. Gesperrte Parameter bleiben in jeder Generation unangetastet.
        </p>

        <button
          type="button"
          onClick={() => seedFrom(doc)}
          className="mb-2 flex w-full items-center justify-center gap-1.5 rounded-md bg-accent-500 px-3 py-1.5 text-[12px] font-medium text-ink-950 transition-colors hover:bg-accent-400"
        >
          <Sparkles size={12} />
          {base ? 'Neu starten' : 'Aus der Komposition starten'}
        </button>

        {base && (
          <button
            type="button"
            onClick={advance}
            disabled={running}
            className={clsx(
              'mb-3 flex w-full items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors',
              running ? 'bg-ink-750 text-ink-500' : 'bg-ink-700 text-ink-100 hover:bg-ink-600',
            )}
          >
            {running ? <Loader2 size={12} className="animate-spin" /> : <Dna size={12} />}
            {chosen > 0 ? `Weiter mit ${chosen}` : 'Nochmal würfeln'}
            <span className="font-mono text-[10px] text-ink-400">↵</span>
          </button>
        )}

        <Slider
          label="Mutation"
          value={config.mutation}
          onChange={(mutation) => setConfig({ mutation })}
          min={0.05}
          max={1}
          step={0.01}
          defaultValue={0.35}
          precision={2}
          tooltip="Wie viele Parameter sich ändern und wie stark. Niedrig heisst behutsam variieren, hoch heisst weit springen."
        />
        <Slider
          label="Population"
          value={config.populationSize}
          onChange={(populationSize) => setConfig({ populationSize: Math.round(populationSize) })}
          min={4}
          max={24}
          step={1}
          defaultValue={12}
          precision={0}
        />

        <div className="mt-2 space-y-0.5 border-t border-ink-800 pt-2">
          <Toggle
            checked={config.elitism}
            onChange={(elitism) => setConfig({ elitism })}
            label="Gewählte behalten"
            tooltip="Die gewählten Bilder erscheinen unverändert in der nächsten Generation, damit nichts durch Zufall verloren geht."
          />
          <Toggle
            checked={config.mutateSeeds}
            onChange={(mutateSeeds) => setConfig({ mutateSeeds })}
            label="Seeds mutieren"
            tooltip="Neue Zufallsströme — dieselben Parameter, andere Ausführung."
          />
          <Toggle
            checked={config.mutatePalettes}
            onChange={(mutatePalettes) => setConfig({ mutatePalettes })}
            label="Paletten mutieren"
          />
          <Toggle
            checked={config.mutateText}
            onChange={(mutateText) => setConfig({ mutateText })}
            label="Typografie mitverändern"
            tooltip="Auch Schriftgrösse, Laufweite und Verzerrung des Textes werden variiert. Ohne das bleibt der Satz stehen und nur die Technik entwickelt sich."
          />
          <Toggle
            checked={config.mutateTechniques}
            onChange={(mutateTechniques) => setConfig({ mutateTechniques })}
            label="Techniken tauschen"
            tooltip="Ersetzt gelegentlich eine Technik durch eine andere aus derselben Kategorie."
          />
        </div>

        <div className="mt-2 border-t border-ink-800 pt-2">
          <Row label="Kachelgrösse">
            <input
              type="range"
              min={120}
              max={400}
              value={thumbSize}
              onChange={(e) => setThumbSize(Number(e.target.value))}
              className="w-full accent-accent-500"
            />
          </Row>
        </div>

        {lineage.length > 0 && (
          <div className="mt-3 border-t border-ink-800 pt-2">
            <div className="panel-label mb-1.5">Verlauf</div>
            <div className="flex items-end gap-[2px]">
              {lineage.map((entry) => (
                <div
                  key={entry.generation}
                  title={`Generation ${entry.generation}: ${entry.chosen} gewählt`}
                  className={clsx('flex-1 rounded-sm', entry.chosen ? 'bg-accent-500' : 'bg-ink-700')}
                  style={{ height: 4 + entry.chosen * 3 }}
                />
              ))}
            </div>
            <p className="mt-1 text-[10px] text-ink-600">
              Balkenhöhe = wie viele Bilder je Generation gewählt wurden.
            </p>
          </div>
        )}

        {base && (
          <button
            type="button"
            onClick={reset}
            className="ghost-button mt-3 w-full justify-center gap-1.5 py-1.5 text-[11px]"
          >
            <RotateCcw size={11} />
            Zurücksetzen
          </button>
        )}
      </aside>

      {/* ── Population ────────────────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col bg-ink-950">
        {population.length > 0 && (
          <div className="flex h-8 shrink-0 items-center gap-3 border-b border-ink-800 bg-ink-900 px-3 font-mono text-[10px] text-ink-400">
            <span>
              {done}/{population.length} gerendert
            </span>
            {progress.failed > 0 && <span className="text-amber-400">{progress.failed} fehlgeschlagen</span>}
            <span className={chosen ? 'text-accent-500' : ''}>{chosen} gewählt</span>
            {chosen > 0 && (
              <button type="button" onClick={chooseNone} className="ghost-button h-5 gap-1 px-1.5 py-0 text-[10px]">
                <X size={9} />
                Auswahl leeren
              </button>
            )}
            <span className="ml-auto text-ink-600">Klick = wählen · Enter = nächste Generation</span>
          </div>
        )}

        {population.length === 0 ? (
          <div className="flex flex-1 items-center justify-center p-8">
            <div className="max-w-[420px] text-center">
              <Dna size={28} className="mx-auto mb-3 text-ink-700" />
              <p className="text-[13px] text-ink-200">Züchten statt einstellen</p>
              <p className="mt-2 text-[11px] leading-relaxed text-ink-500">
                Aus der aktuellen Komposition entsteht eine Population von Varianten. Du wählst aus, was dir
                gefällt, und daraus wächst die nächste Generation — Kreuzung entlang der Parameter, Mutation
                innerhalb genau der Explorationsbereiche, die du im Studio siehst.
              </p>
              <p className="mt-2 text-[11px] leading-relaxed text-ink-600">
                Nichts zu wählen ist auch eine Antwort: dann wird noch einmal um denselben Ausgangspunkt gewürfelt.
              </p>
            </div>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            <div
              className="grid gap-2"
              style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${thumbSize}px, 1fr))` }}
            >
              {population.map((variant) => (
                <div
                  key={variant.id}
                  className={clsx(
                    'group/tile relative overflow-hidden rounded-lg border transition-colors',
                    variant.favourite
                      ? 'border-accent-500 bg-accent-500/5'
                      : 'border-ink-800 bg-ink-900 hover:border-ink-600',
                  )}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      toggleChosen(variant.id);
                      // Give focus back, or the next Enter re-triggers this tile instead
                      // of breeding the generation.
                      e.currentTarget.blur();
                    }}
                    className="block w-full"
                    title={
                      variant.doc.layers
                        .filter((l) => l.techniqueId !== TEXT_TECHNIQUE_ID)
                        .map((l) => getTechnique(l.techniqueId)?.name ?? l.techniqueId)
                        .join(' + ') || 'Nur Text'
                    }
                  >
                    <div
                      className="flex items-center justify-center bg-ink-950"
                      style={{ aspectRatio: `${variant.doc.canvas.width} / ${variant.doc.canvas.height}` }}
                    >
                      {variant.url ? (
                        <img src={variant.url} alt="" className="h-full w-full object-contain" />
                      ) : variant.error ? (
                        <span className="px-2 text-center text-[10px] leading-tight text-amber-300">
                          {variant.error}
                        </span>
                      ) : (
                        <Loader2 size={16} className="animate-spin text-ink-700" />
                      )}
                    </div>
                  </button>

                  {variant.favourite && (
                    <span className="pointer-events-none absolute left-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-accent-500 text-ink-950">
                      <Check size={12} strokeWidth={3} />
                    </span>
                  )}

                  <button
                    type="button"
                    title="Diese Variante ins Studio übernehmen"
                    onClick={() => replaceDocument(structuredClone(variant.doc), `Evolution Gen ${generation}`)}
                    className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-md bg-ink-900/85 text-ink-300 opacity-0 transition-opacity hover:text-ink-100 group-hover/tile:opacity-100"
                  >
                    <SquareArrowOutUpRight size={11} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
